import { Popover } from "@progress/kendo-react-tooltip";
import { Copy } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import useOnClickOutside from "@/hooks/useOnClickOutside";

import "./style.scss";

interface Props {
  children?: React.ReactNode;
  copiedTitle?: string;
  copyToText?: string;
  iconFontSize?: number;
  iconStyle?: React.CSSProperties;
  style?: React.CSSProperties;
  position?: "left" | "right";
  title?: string;
  /**
   * Accessible name for the copy button, e.g. "Copy server ID". Defaults to
   * the generic "Copy to Clipboard"; pass one when several copy buttons share
   * a screen, so each says what it copies.
   */
  label?: string;
  /**
   * Visible text inside the button ("Copy schema"). Renders a small outline
   * button instead of the bare icon; `label`, when given, must start with or
   * contain this text so the spoken name matches what is shown.
   */
  text?: string;
  testId?: string;
}

/**
 * Copies with the async Clipboard API. Not `execCommand("copy")`: that needs a
 * selection outside the current focus scope, so inside a focus-holding drawer
 * it fails and its fallback is a blocking `window.prompt`. A refused write here
 * is reported as a failure instead.
 */
const writeToClipboard = async (value: string): Promise<boolean> => {
  try {
    if (!navigator.clipboard?.writeText) return false;
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
};

/**
 * A copy button beside optional content. The icon is a real `<button>`, so it
 * is focusable, activates on Enter/Space and has an accessible name; the
 * "copied" confirmation is also announced through a polite live region.
 *
 * @example
 * String Children
 *
 * <CopyToClipboard> Hello text </CopyToClipboard>
 * @example
 * HTML Children
 *
 * <CopyToClipboard copiedTitle="my text" copyToText="non html text">
 *  <span>Hello HTML Text </span>
 * </CopyToClipboard>
 *
 */

const CopyToClipboard: React.FC<Props> = ({
  copyToText,
  title: textTitle,
  children,
  iconFontSize = 18,
  copiedTitle,
  iconStyle = {},
  style = {},
  position = "left",
  label,
  text,
  testId,
}) => {
  const { t } = useTranslation();
  const anchor = React.useRef<HTMLButtonElement>(null);
  // `undefined` until a copy is attempted; then whether it succeeded.
  const [copied, setCopied] = React.useState<boolean>();
  const show = copied !== undefined;
  useOnClickOutside(anchor, () => setCopied(undefined));

  const successTitle = copiedTitle
    ? t("common.textCopied", { text: copiedTitle })
    : t("common.copiedToClipboard");
  const title = copied === false ? t("common.copyFailed") : successTitle;
  const buttonLabel = label ?? text ?? t("copyToClipboard.title");
  const copy = () => {
    void writeToClipboard(copyToText || (children as string)).then(setCopied);
  };
  const icon = (
    <Copy
      size={14}
      strokeWidth={2}
      aria-hidden="true"
      focusable="false"
      style={{ fontSize: iconFontSize }}
    />
  );

  if (!title) return null;

  return (
    <span className="tlx-copy-to-clipboard" title={textTitle} style={style}>
      {position === "right" && children}
      {text ? (
        // Kendo's own button markup, so it matches an outline `<Button>`.
        <button
          type="button"
          ref={anchor}
          onClick={copy}
          aria-label={label}
          className="tlx-copy-button k-button k-button-sm k-rounded-md k-button-outline k-button-outline-base"
          style={iconStyle}
          data-test-id={testId}
        >
          <span className="k-button-icon">{icon}</span>
          <span className="k-button-text">{text}</span>
        </button>
      ) : (
        <button
          type="button"
          ref={anchor}
          onClick={copy}
          title={buttonLabel}
          aria-label={buttonLabel}
          className="tlx-copy-icon"
          style={iconStyle}
          data-test-id={testId}
        >
          {icon}
        </button>
      )}
      {position === "left" && children}
      {/* The popover is visual only; this region announces the same message.
          It stays mounted so assistive tech is already watching it. */}
      <span className="k-sr-only" role="status" aria-live="polite">
        {show ? title : ""}
      </span>
      <Popover
        className="k-custom-popover"
        show={show}
        anchor={anchor.current && anchor.current}
        position="top"
      >
        <span className={copied ? "success" : "error"} aria-hidden="true">
          <span
            className={`k-icon k-font-icon ${copied ? "k-i-check-circle" : "k-i-x-circle"}`}
            style={{ fontSize: 16, marginRight: 2, display: "inline-block" }}
          />
          {title}
        </span>
      </Popover>
    </span>
  );
};

export default CopyToClipboard;
